/* The new header search's matches that fit the shopper's machine: Shopify's search filtered to it, run alongside predictive search. */
(function () {
  'use strict';

  if (theme.searchMachineResults) return;

  const MACHINE_FILTER = 'filter.p.m.rpm_fitment.compatible_machines';
  const LIST_SELECTOR = '.results--products [data-visible-limit]';
  const OPTION_SELECTOR = 'li[role="option"]';
  const RESULTS = '.predictive-search-results';
  const INCOMPLETE_ATTR = 'data-search-incomplete';
  const FIELDS_ATTR = 'data-search-fields';
  // A row only this search found, so PredictiveSearch can count it when chosen (theme.js SEARCH_MACHINE_MATCH).
  const MATCH_ATTR = 'data-search-machine-match';
  // Predictive search's typo tolerance needs a word's first four letters typed correctly (Predictive Search API reference).
  const TYPO_FREE_LETTERS = 4;
  // Started with the predictive request and lighter than it, so it usually lands first; past this, results show without it.
  const GRACE_MS = 300;
  const TIMED_OUT = Symbol('machine results timed out');
  // Kept per search (term and machine) for the page, as PredictiveSearch keeps its responses, so a repeat doesn't race them again.
  const CACHE_LIMIT = 8;
  const cache = new Map();

  // Predictive search ranks by words alone, so a machine's parts can miss its ten candidates ("exhaust" for a Teryx H2 is all X3).
  function start(search, searchTerm, signal) {
    const base = search.dataset.machineResultsUrl;
    const machine = activeMachine();
    const words = wordsOf(searchTerm);
    if (!base || !machine || !words.length) return null;
    const url = searchUrl(base, searchTerm, machine.id);
    if (cache.has(url)) return Promise.resolve(copies(cache.get(url)));
    return fetch(url, { signal })
      .then((response) => (response.ok ? response.text() : null))
      .then((html) => (html === null ? null : remember(url, rowsOf(html).filter((row) => namesEvery(row, words)))))
      .catch(() => null);
  }

  // Shopify's search reads a product's description and tags too, where a part may only mention a word ("for the Supercharged H2"); a part
  // joins only if the fields that name it hold every word, the last one as typed so far, as Shopify matches a partial last word.
  function namesEvery(row, words) {
    const fields = wordsOf(row.getAttribute(FIELDS_ATTR));
    const last = words.length - 1;
    return words.every((word, i) => fields.some((field) => sameWord(word, field) || (i === last && field.startsWith(word))));
  }

  // As Shopify matches a word: in its singular or plural form (Help Center, search behavior), or with one letter wrong, missing, extra
  // or swapped past its first four (Predictive Search API, typo tolerance), which keeps model names and years ("x3", "2024") exact.
  function sameWord(word, field) {
    const typed = singular(word);
    const named = singular(field);
    if (typed === named) return true;
    const prefix = typed.slice(0, TYPO_FREE_LETTERS);
    return typed.length > TYPO_FREE_LETTERS && named.startsWith(prefix) && oneEditApart(typed, named);
  }

  // Lucene's EnglishMinimalStemmer (Harman's S-stemmer): "batteries" reads as "battery", "exhausts" as "exhaust", "glass" stays.
  function singular(word) {
    if (word.length < 3 || !word.endsWith('s') || /[us]s$/.test(word)) return word;
    if (/[^ae]ies$/.test(word)) return `${word.slice(0, -3)}y`;
    return /[iaoe]es$/.test(word) ? word : word.slice(0, -1);
  }

  // Two different words one letter wrong, missing or extra apart, or two neighbouring letters swapped.
  function oneEditApart(a, b) {
    if (Math.abs(a.length - b.length) > 1) return false;
    let i = 0;
    while (i < a.length && a[i] === b[i]) i += 1;
    if (a.length > b.length) return a.slice(i + 1) === b.slice(i);
    if (a.length < b.length) return a.slice(i) === b.slice(i + 1);
    return a.slice(i + 1) === b.slice(i + 1) || (a[i] === b[i + 1] && a[i + 1] === b[i] && a.slice(i + 2) === b.slice(i + 2));
  }

  // Split as the categories split them (search-model-collections.js); without that module, no words and so no machine results.
  function wordsOf(text) {
    const models = theme.searchModelCollections;
    return models ? models.wordsOf(text) : [];
  }

  // The matches join the candidates hidden, for search-fitment.js to rank; results that didn't get them aren't kept.
  async function add(section, pending) {
    if (!pending) return;
    const rows = await withinGrace(pending);
    const list = section.querySelector(LIST_SELECTOR);
    if (!Array.isArray(rows)) markIncomplete(section);
    if (!Array.isArray(rows) || !list) return;
    const known = new Set(Array.from(list.children).map(productPath));
    rows.filter((row) => !known.has(productPath(row))).forEach((row) => {
      row.hidden = true;
      row.setAttribute(MATCH_ATTR, '');
      list.appendChild(row);
    });
  }

  // Most recent last, so the oldest goes once the cache is full; sections are edited, so each use gets copies.
  function remember(url, rows) {
    cache.delete(url);
    cache.set(url, rows);
    if (cache.size > CACHE_LIMIT) cache.delete(cache.keys().next().value);
    return copies(rows);
  }

  function copies(rows) {
    return rows.map((row) => row.cloneNode(true));
  }

  function withinGrace(pending) {
    let timer;
    const grace = new Promise((resolve) => { timer = setTimeout(resolve, GRACE_MS, TIMED_OUT); });
    return Promise.race([pending, grace]).finally(() => clearTimeout(timer));
  }

  function markIncomplete(section) {
    const results = section.querySelector(RESULTS);
    if (results) results.setAttribute(INCOMPLETE_ATTR, '');
  }

  function searchUrl(url, searchTerm, machineId) {
    const target = new URL(url, window.location.origin);
    target.searchParams.set('q', searchTerm);
    target.searchParams.set(MACHINE_FILTER, machineId);
    return target.toString();
  }

  function rowsOf(html) {
    const holder = document.createElement('template');
    holder.innerHTML = html;
    return Array.from(holder.content.querySelectorAll(OPTION_SELECTOR));
  }

  // A product by its path: predictive search and the search page tag the same link with different tracking parameters.
  function productPath(row) {
    const link = row.querySelector('a');
    try {
      return new URL(link.getAttribute('href'), window.location.origin).pathname;
    } catch (e) {
      return '';
    }
  }

  // search-fitment.js's machine: none while a shared link's machine is still being looked up.
  function activeMachine() {
    const fitment = theme.searchFitment;
    const status = fitment && fitment.machineStatus ? fitment.machineStatus() : null;
    return status && status.machine && status.machine.id ? status.machine : null;
  }

  theme.searchMachineResults = { start, add };
})();
